import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useTranslation, Trans } from 'react-i18next';
import { useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";
import "../sideMenu/style.scss";
import "./style.scss";
import LoadMethod from "../loadMethod/index";
import LoadGrid from "../grid/loadGrid";
import LoadPV from "../PV";
import LoadBattery from "../battery/index";
import ComponentProgressBar from "../componentProgressBar";
import MGAAPI from "../../api/index";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { activeTabAtom } from "../../utils/atoms/activeTabAtom";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import { getDefaultComponentsState } from "../../utils/atoms/defaultComponentsState";
import LoadGenset from "../genset";
import { selectedCurrentComponent } from "../../utils/atoms/selectedComponentAtom";
import ManageProgressBar from "../manageProgressBar";
import { componentDTO } from "../../constants/statusDTO";
import { getComponentDisplayName } from "../../constants/helper";
import SnackBarJs from "../SnackBar.js";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom.js";
import { AccordianSkeleton } from "../Skeleton/index.js";
import { componentsLoaderAtom } from "../../utils/atoms/componentsLoader.js";
import { componentStatusAtom } from "../../utils/atoms/componentStatus.js";
import { saveTriggerAtom } from "../../utils/atoms/saveTriggerAtom.js";
import SEConfirmModal from "../common/SEConfirmModal";
import {
  LoadingOverlay,
  useMantineColorScheme,
  Stack,
  Text,
} from "@mantine/core";
import { SeLoading } from "@se/web-ui-react";
import LoadController from "../controller/loadController/index.js";
import EVCSMethod from "../evcs/index.js";
import _ from "lodash";
import cloneDeep from "lodash/cloneDeep";
import { tourAtom } from "../../utils/atoms/tourAtom.js";
import {
  useAssessmentRouteParams,
  useCurrentDesignComponentLists,
  useCurrentDesignVersion,
  useDesignVersionStatusAndComponents,
  isDesignVersionReadOnly,
} from "../../constants/helper.js";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom.js";
import { defaultComponentsAtom } from "../../utils/atoms/defaultComponentAtom.js";
import { navigateToComponentAtom } from "../../utils/atoms/navigateToComponentAtom.js";
import { getErrorMessage } from "../../utils/errorUtils.js";
import displayError from "../../constants/utils.js";

const MGA_API = new MGAAPI();

export const makeFetchKey = (componentId, timestamp = Date.now()) =>
  `${componentId || "unknown"}-${timestamp}`;

// Components addressable via the `component` URL query param. Anything else
// (missing, misspelled, or a component not present in the design) falls back to "load".
const VALID_COMPONENT_KEYS = [
  "load",
  "grid",
  "pv",
  "battery",
  "genset",
  "controller",
  "evcs",
];

const LoadProject = React.memo(function LoadProject({
  loading,
  setSnackBar,
  snackBarRef,
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Guard setProjectComponents for currentComponent
  const [projectDetails, setProjectDetails] = useAtom(projectDetailsAtom);
  const [componentDetails, setProjectComponents] = useAtom(componentsAtom);
  const [defaultComponents, ] = useAtom(defaultComponentsAtom);
  const safeSetCurrentComponent = (newComponent) => {
    setProjectComponents((prev) => {
      if (prev.currentComponent === newComponent) return prev;
      return { ...prev, currentComponent: newComponent };
    });
  };
  const getLoadInfo = useDesignVersionStatusAndComponents( projectDetails?.designWithVersionsDTOList ,projectDetails?.designComponentsList)
  const prevProjectDetailsRef = useRef();
  const initializedRef = useRef(false);
  // Keyed per componentType so concurrent fetches for different components
  // (e.g. the load-info prefetch and an explicitly requested initial tab
  // like "grid") don't mark each other's in-flight responses as stale.
  const activeFetchKeyRef = useRef({});
  // Tracks mount state — blocks stale async API responses from a previous
  // project overwriting the atom after this component unmounts
  const isMountedRef = useRef(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  // Resolve the component tab requested via the `?component=` URL query param.
  // Falls back to "load" for anything missing/unrecognized; final validation
  // against the components actually present in this design happens once the
  // design data loads (see initializeProgress).
  const getUrlComponentKey = useCallback(() => {
    const raw = (searchParams.get("component") || "").toLowerCase();
    return VALID_COMPONENT_KEYS.includes(raw) ? raw : "load";
  }, [searchParams]);

  const [activeComponent, setActiveComponent] = useState(() => getUrlComponentKey());
  const [componentStatus, setComponentStatus] = useAtom(componentStatusAtom);
  const [, setSelectedComponent] = useAtom(selectedCurrentComponent);
  const [componentsLoader, setComponentsLoader] = useAtom(componentsLoaderAtom);
  const [stepProgress, setStepProgress] = useState({
    load: false,
    grid: false,
    controller: false,
    pv: false,
    battery: false,
    genset: false,
    evcs: false,
  });
    const [userScope] = useAtom(userScopeAtom);
  const [navigateToComponent, setNavigateToComponent] = useAtom(navigateToComponentAtom);

  const [showNextSteps, setShowNextSteps] = useState(false);
  const [addedSteps, setAddedSteps] = useState(["load"]);
  const [gridComponent, setGridComponent] = useState(null);
  const [pvComponent, setPVComponent] = useState(null);
  const [componentDTOList, setComponentDTOList] = useState([]);
  const [initialSelectedBuildingType, setInitialSelectedBuildingType] =
    useState("");
  const [activeState] = useAtom(activeTabAtom);
  const tariffRef = useRef();
  const [, setAddedStepsSeries] = useAtom(addedStepsAtom);
  const tariffEnergyRef = useRef();
  const tariffTouRef = useRef();
  const dcTariffRef = useRef();
  const dcGenabilityTariffRef = useRef()
  const [loadingComp, setLoadingComp] = useState(false);
  const [lastComponent, setLastComponent] = useState({});
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );
  const prevSelectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    prevProjectDetailsRef?.current?.designComponentsList
  );
  const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList)
  const isVersionReadOnly = isDesignVersionReadOnly(selectedVersion?.versionStatus);
  const prevDesignVersionIdRef = useRef();
  // Memoize filteredComponentDTOList
  const filteredComponentDTOList = useMemo(() => {
    return selectedDesignAndDesignVersion.componentDTOList.filter(
      (component) => component.componentType.toLowerCase() !== "thermalload"
    );
  }, [selectedDesignAndDesignVersion.componentDTOList]);

  const isElectricalLoadPresent = useMemo(() => {
    return selectedDesignAndDesignVersion.componentDTOList.filter(
      (component) => component.componentType.toLowerCase() === "electricalload"
    );
  }, [selectedDesignAndDesignVersion.componentDTOList]);

  const [aboutComponentSelected, setAboutComponentSelected] = useState("");

  const selectComponentHandler = (component, newComponent = false) => {
    setComponentStatus((prev) => ({
      ...prev,
      [componentDetails?.currentComponent]: {
        completed: true,
        isEditing: false,
      },
    }));
    if (!newComponent && component) {
      let thermalFound;
      if (component.toLowerCase() === "load") {
        thermalFound = selectedDesignAndDesignVersion.componentDTOList.filter(
          (a) => a.componentType.toLowerCase() === "electricalload"
        );
      }
      const componentFound =
        thermalFound?.length > 0
          ? thermalFound.filter(
              (comp) =>
                componentDTO[comp.componentType] === componentDTO[component]
            )
          : filteredComponentDTOList.filter(
              (comp) =>
                componentDTO[comp.componentType] === componentDTO[component]
            );
      if (componentFound.length > 0) {
        setActiveComponent(component.toLowerCase());
        setComponentsLoader(true);
        fetchLastComponentDetails(componentFound?.[0]).then((res) => {
          safeSetCurrentComponent(componentDTO[component.toLowerCase()]);
          if (!addedSteps.includes(component)) {
            setAddedSteps([...addedSteps, component]);
            setAddedStepsSeries([...addedSteps, component]);
          }
          setShowNextSteps(false);
        });
      } else {
        // Component not found in backend list; ensure a default entry exists before switching
        setProjectComponents((prev) => {
          const compKey = component.toLowerCase();
          const next = { ...prev };
          if (next[compKey] === undefined) {
            const template = (defaultComponents && defaultComponents[compKey]
              ? cloneDeep(defaultComponents[compKey])
              : cloneDeep(getDefaultComponentsState()[compKey] ?? {}));
            next[compKey] = template;
          }
            next.currentComponent = componentDTO[compKey];
          return next;
        });
      setActiveComponent(component.toLowerCase());
        if (!addedSteps.includes(component)) {
          setAddedSteps([...addedSteps, component]);
          setAddedStepsSeries([...addedSteps, component]);
        }
        setShowNextSteps(false);
      }
    } else if (newComponent) {
      setProjectComponents((prev) => {
        const compKey = component.toLowerCase();
        const next = { ...prev };
        if (next[compKey] === undefined || compKey === "pv" || compKey === "battery" || compKey === "genset" || compKey === "controller" || compKey === "evcs") {
          const template = (defaultComponents && defaultComponents[compKey]
            ? cloneDeep(defaultComponents[compKey])
            : cloneDeep(getDefaultComponentsState()[compKey] ?? {}));
          next[compKey] = template;
        }
        next.currentComponent = componentDTO[compKey];
        return next;
      });
      setComponentStatus((prev) => ({
        ...prev,
        [component.toLowerCase()]: {
          completed: false,
          isEditing: true,
        },
      }));
      setActiveComponent(component.toLowerCase());
      if (!addedSteps.includes(component)) {
        setAddedSteps([...addedSteps, component]);
        setAddedStepsSeries([...addedSteps, component]);
      }
      setShowNextSteps(false);
    }
  };
  // Handle cross-component navigation requests (e.g. from genset validation modals)
  useEffect(() => {
    if (navigateToComponent) {
      const compKey = navigateToComponent.toLowerCase();
      // If the component is not present in the saved design list it means it was never saved
      // or has been deleted. In that case treat it as a new component so that componentStatus
      // is correctly set to { isEditing: true, completed: false }.
      const existsInBackend = filteredComponentDTOList.some(
        (c) => componentDTO[c.componentType] === compKey
      );
      if (!existsInBackend) {
        // Force status to editing so the panel is not shown as "completed"
        setComponentStatus((prev) => ({
          ...prev,
          [compKey]: { isEditing: true, completed: false },
        }));
      }
      selectComponentHandler(navigateToComponent, !existsInBackend);
      setNavigateToComponent(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateToComponent, filteredComponentDTOList]);

  const initializeProgress = (isVersionSwitch = false) => {
    setComponentsLoader(true);
    const updatedProgress = { ...stepProgress };
    if (filteredComponentDTOList.length === 0) {
      updatedProgress.load = false;
      setStepProgress(updatedProgress);
      setAddedSteps(["load"]);
      setAddedStepsSeries(["load"]);
      setComponentsLoader(false);
      return;
    }
    let projectCompOrder = [];
    let componentStatusCheck = { ...componentStatus };
    setComponentDTOList(selectedDesignAndDesignVersion.componentDTOList);
    updatedProgress.load = true;
    projectCompOrder.push("load");
    componentStatusCheck.load.completed =
      selectedDesignAndDesignVersion.componentDTOList.length === 0
        ? false
        : true;
    componentStatusCheck.load.isEditing = false;

    selectedDesignAndDesignVersion.componentDTOList.forEach((component) => {
      const { componentType } = component;
      if (componentType.toLowerCase() === componentDTO["battery"]) {
        updatedProgress[componentDTO["battery"]] = true;
        componentStatusCheck[componentDTO["battery"]].completed = true;
        componentStatusCheck[componentDTO["battery"]].isEditing = false;

        projectCompOrder.push("battery");
      } else if (componentType.toLowerCase() === componentDTO["pv"]) {
        updatedProgress[componentDTO["pv"]] = true;
        componentStatusCheck[componentDTO["pv"]].completed = true;
        componentStatusCheck[componentDTO["pv"]].isEditing = false;
        projectCompOrder.push("pv");
      } else if (
        [
          "Baseload genset",
          "multi genset",
          "single genset",
          "baseload genset",
        ].includes(componentType.toLowerCase())
      ) {
        updatedProgress[componentDTO["genset"]] = true;
        componentStatusCheck[componentDTO["genset"]].completed = true;
        componentStatusCheck[componentDTO["genset"]].isEditing = false;

        projectCompOrder.push("genset");
      } else if (componentType.toLowerCase() === componentDTO["grid"]) {
        updatedProgress[componentDTO["grid"]] = true;
        componentStatusCheck[componentDTO["grid"]].completed = true;
        componentStatusCheck[componentDTO["grid"]].isEditing = false;

        projectCompOrder.push("grid");
      } else if (componentType.toLowerCase() === componentDTO["controller"]) {
        updatedProgress[componentDTO["controller"]] = true;
        componentStatusCheck[componentDTO["controller"]].completed = true;
        componentStatusCheck[componentDTO["controller"]].isEditing = false;

        projectCompOrder.push("controller");
      } else if (componentType.toLowerCase() === componentDTO["evcs"]) {
        updatedProgress[componentDTO["evcs"]] = true;
        componentStatusCheck[componentDTO["evcs"]].completed = true;
        componentStatusCheck[componentDTO["evcs"]].isEditing = false;

        projectCompOrder.push("evcs");
      }
    });

    // Land on whichever component the URL asked for on mount (?component=grid,
    // etc.), as long as it actually exists in this design; otherwise fall back
    // to "load". Prefer the current activeComponent over re-reading the URL
    // here: if the user already clicked a different tab while the design was
    // still loading, activeComponent reflects that click and must win over
    // whatever was originally in the URL. On a genuine version switch,
    // though, activeComponent is stale from the PREVIOUS version and must
    // not carry over - trust only the URL (already reset to "load" by the
    // version-switch navigation) in that case.
    const requestedComponent = isVersionSwitch
      ? getUrlComponentKey()
      : activeComponent || getUrlComponentKey();
    const initialComponent = projectCompOrder.includes(requestedComponent)
      ? requestedComponent
      : "load";

    setActiveComponent(initialComponent);
    safeSetCurrentComponent(initialComponent);
    setStepProgress(updatedProgress);
    setAddedSteps(projectCompOrder);
    setAddedStepsSeries(projectCompOrder);
    setComponentStatus((prev) => ({
      ...prev,
      ...componentStatusCheck,
    }));

    // If landing directly on a non-"load" tab (e.g. from a reload/shared URL),
    // fetch that component's own data the same way a manual tab click would.
    // The "load info" prefetch below (via setLastComponent) is deferred until
    // this finishes so the two requests never race and clobber each other's
    // in-flight state.
    if (initialComponent !== "load") {
      const initialComponentEntry = filteredComponentDTOList.find(
        (comp) => componentDTO[comp.componentType] === initialComponent
      );
      if (initialComponentEntry) {
        setComponentsLoader(true);
        fetchLastComponentDetails(initialComponentEntry)
          .catch(() => {})
          .finally(() => {
            setComponentsLoader(false);
            setLastComponent(filteredComponentDTOList[0]);
          });
      } else {
        setLastComponent(filteredComponentDTOList[0]);
      }
    } else {
      setLastComponent(filteredComponentDTOList[0]);
    }
    //  fetchLastComponentDetails({
    //     ...filteredComponentDTOList[0],
    //   });

    // setProjectComponents((prev) => ({
    //   ...prev,
    //   currentComponent:
    //     componentDTO[
    //       filteredComponentDTOList[
    //         filteredComponentDTOList.length - 1
    //       ].componentType.toLowerCase()
    //     ],
    // }));
  };

  // Keep the `?component=` URL query param in sync with whichever tab is active,
  // so a page reload (or a shared link) lands back on the same component instead
  // of always defaulting to "load". Safe to sync unconditionally now:
  // createComponentStatus no longer touches activeComponent during the
  // pre-data placeholder phase, so this never fires with a stale "load".
  useEffect(() => {
    if (!activeComponent) return;
    const current = (searchParams.get("component") || "").toLowerCase();
    if (current !== activeComponent) {
      const next = new URLSearchParams(searchParams);
      next.set("component", activeComponent);
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeComponent]);

  // The tab bar strips "component" from the URL when leaving Design (see
  // manageProgressBar), but activeComponent itself stays whatever it was
  // since this component doesn't remount on tab switch. Whenever Design is
  // showing and the URL genuinely has no "component" (i.e. it was just
  // stripped, or this is a fresh visit), reset to "load" instead of
  // silently re-showing the last one that was open. manageProgressBar now
  // strips it via react-router's setSearchParams, so this stays in sync.
  useEffect(() => {
    if (activeState !== "DESIGN" || activeComponent === "load") return;
    if (!searchParams.get("component")) {
      setActiveComponent("load");
      safeSetCurrentComponent("load");
    }
  }, [activeState, activeComponent, searchParams]);


  useEffect(() => {
    if (
      selectedDesignAndDesignVersion?.componentDTOList?.length > 0 &&
      lastComponent &&
      lastComponent?.componentId
    ) {
      fetchLastComponentDetails({
        ...isElectricalLoadPresent[0],
      });
    }
  }, [lastComponent]);

  // Separate function to update projectDetails atom with componentAttributes from API response
  const updateProjectDetailsWithComponentAttributes = useCallback((apiResponse, designVersionId) => {
    
    // Search through designComponentsList to find the matching designVersionComponents
    setProjectDetails((prevDetails) => {
      const updatedDesignComponentsList = prevDetails?.designComponentsList?.map((design) => {
        // Find the matching design version
        const updatedDesignVersionComponents = design?.designVersionComponents?.map((version) => {
          if (String(version.designVersionId) === String(designVersionId)) {
            // Found matching design version, now update the component in componentDTOList
            const updatedComponentDTOList = version?.componentDTOList?.map((component) => {
              if (String(component.componentId) === String(apiResponse?.componentId)) {
                // Found matching component, copy entire response but focus on componentAttributes
                return {
                  ...component,
                  // Copy the entire apiResponse to preserve all properties, not just componentAttributes
                  ...apiResponse,
                };
              }
              return component;
            });
            return {
              ...version,
              componentDTOList: updatedComponentDTOList || version?.componentDTOList,
            };
          }
          return version;
        });
        return {
          ...design,
          designVersionComponents: updatedDesignVersionComponents || design?.designVersionComponents,
        };
      });
      return {
        ...prevDetails,
        designComponentsList: updatedDesignComponentsList || prevDetails?.designComponentsList,
      };
    });
  }, []);

  const fetchLastComponentDetails = useCallback((lastComponent) => {
    // Track latest fetch to ignore stale responses when user switches components quickly
    const fetchKey = makeFetchKey(lastComponent?.componentId);
    const fetchTypeKey = (lastComponent?.componentType || "unknown").toLowerCase();
    activeFetchKeyRef.current[fetchTypeKey] = fetchKey;
    const isStale = (key) => activeFetchKeyRef.current[fetchTypeKey] !== key || !isMountedRef.current;

    // Only call getComponentInfo if lastComponent is ElectricalLoad or ThermalLoad
    const isElecLoadComponent = 
      lastComponent?.componentType?.toLowerCase() === "electricalload" 
    const isLoad = lastComponent?.componentType?.toLowerCase() === "electricalload"  || lastComponent?.componentType?.toLowerCase() === "thermalload";

    const maxRetries = 3;
    const retryDelay = 1000; // ms
    const electricalLoadSelected = isLoad ? getLoadInfo?.hasOnlyElectricalLoad : false;
    const hasBothLoads =  isLoad ? getLoadInfo?.hasBothLoads : false;
    if (electricalLoadSelected) {
      const fetchDetails = (component, retryCount = 0, resolve, reject) => {
        const { componentId, designId, designVersionId } = component;

        MGA_API.getComponentInfo(designId, designVersionId, componentId)
          .then((res) => {
            if (isStale(fetchKey)) return; // ignore stale responses
            setSelectedComponent(res);
            // Update projectDetails atom with componentAttributes from API response
            updateProjectDetailsWithComponentAttributes(res, designVersionId);
            setProjectComponents((prevComponents) => ({
              ...prevComponents,
              [componentDTO[res.componentType].toLowerCase()]: res,
            }));
            setComponentsLoader(false);
            resolve(res);
          })
          .catch((error) => {
            displayError('error', getErrorMessage(error));
            if (isStale(fetchKey)) return; // ignore stale errors
            if (error?.response?.data?.message && retryCount < maxRetries) {
              setSnackBar({
                type: "error",
                message: `${error.response.data.message}`,
              });
              snackBarRef.current.open = true;
              setTimeout(() => {
                fetchDetails(component, retryCount + 1, resolve, reject);
              }, retryDelay);
            } else {
              reject(error);
            }
          });
      };
      return new Promise((resolve, reject) => {
        fetchDetails(lastComponent, 0, resolve, reject);
      });
    }
    else if(hasBothLoads) {
      const fetchDetails = (component, retryCount = 0, resolve, reject) => {
        const { componentId, designId, designVersionId } = component;

        MGA_API.getComponentInfo(designId, designVersionId, componentId)
          .then((resp) => {
            if (isStale(fetchKey)) return; // ignore stale responses
            setSelectedComponent(resp);
            // Update projectDetails atom with componentAttributes from API response
            updateProjectDetailsWithComponentAttributes(resp, designVersionId);
            setProjectComponents((prevComponents) => ({
              ...prevComponents,
              [componentDTO[resp.componentType].toLowerCase()]: resp,
            }));
            
            // After first API call completes, call the second API if available
            if(getLoadInfo?.componentId) {
              fetchSecondLoadComponent(designId, designVersionId, resolve, reject);
            } else {
              setComponentsLoader(false);
              resolve(resp);
            }
          })
          .catch((error) => {
            if (isStale(fetchKey)) return; // ignore stale errors
            if (error?.response?.data?.message && retryCount < maxRetries) {
              setSnackBar({
                type: "error",
                message: `${error.response.data.message}`,
              });
              snackBarRef.current.open = true;
              setTimeout(() => {
                fetchDetails(component, retryCount + 1, resolve, reject);
              }, retryDelay);
            } else {
              reject(error);
            }
          });
      };

      const fetchSecondLoadComponent = (designId, designVersionId, resolve, reject) => {
        MGA_API.getComponentInfo(designId, designVersionId, getLoadInfo?.componentId)
          .then((res) => {
            if (isStale(fetchKey)) return; // ignore stale responses
            setSelectedComponent(res);
            // Update projectDetails atom with componentAttributes from API response
            updateProjectDetailsWithComponentAttributes(res, designVersionId);
            setComponentsLoader(false);
            resolve(res);
          })
          .catch((error) => {
            if (isStale(fetchKey)) return; // ignore stale errors
            setComponentsLoader(false);
            reject(error);
          });
      };

      return new Promise((resolve, reject) => {
        fetchDetails(lastComponent, 0, resolve, reject);
      });
    }else {
      const fetchDetails = (component, retryCount = 0, resolve, reject) => {
        const { componentId, designId, designVersionId } = component;

        MGA_API.getComponentInfo(designId, designVersionId, componentId)
          .then((res) => {
            if (isStale(fetchKey)) return; // ignore stale responses
            setSelectedComponent(res);
            // Update projectDetails atom with componentAttributes from API response
            updateProjectDetailsWithComponentAttributes(res, designVersionId);
            setProjectComponents((prevComponents) => ({
              ...prevComponents,
              [componentDTO[res.componentType].toLowerCase()]: res,
            }));
            setComponentsLoader(false);
            resolve(res);
          })
          .catch((error) => {
            if (isStale(fetchKey)) return; // ignore stale errors
            if(error?.response?.data?.message){
 setSnackBar({
                type: "error",
                message: `${error.response.data.message}`,
              });
              snackBarRef.current.open = true;
            }
            if (retryCount < maxRetries) {
             
              setTimeout(() => {
                fetchDetails(component, retryCount + 1, resolve, reject);
              }, retryDelay);
            } else {
              reject(error);
            }
          });
      };
      return new Promise((resolve, reject) => {
        fetchDetails(lastComponent, 0, resolve, reject);
      });
    }
  }, [getLoadInfo, ]);

  const createComponentStatus = () => {
    let componentStatusCheck = { ...componentStatus };
    componentStatusCheck.load = {
      isEditing: true,
      completed: false,
    };
    setComponentDTOList(selectedDesignAndDesignVersion.componentDTOList);
    setLastComponent(filteredComponentDTOList[0]);
    setAddedSteps(["load"]);
    setAddedStepsSeries(["load"]);
    // Before init has run once, this is just the pre-data placeholder — the
    // design might still turn out to have a "pv"/"grid"/etc component that the
    // URL explicitly requested, so don't stomp activeComponent with "load"
    // here (initializeProgress reads activeComponent once real data arrives).
    // "load" is only truly forced once we know for certain the design has no
    // components at all (initializedRef.current already true).
    if (initializedRef.current) {
      setActiveComponent("load");
    }
    filteredComponentDTOList.forEach((component) => {
      const { componentType } = component;
      if (componentType.toLowerCase() === componentDTO["battery"]) {
        componentStatusCheck[componentDTO["battery"]].completed = true;
      } else if (componentType.toLowerCase() === componentDTO["pv"]) {
        componentStatusCheck[componentDTO["pv"]].completed = true;
      } else if (
        [
          "Baseload genset",
          "multi genset",
          "single genset",
          "baseload genset",
        ].includes(componentType.toLowerCase())
      ) {
        componentStatusCheck[componentDTO["genset"]].completed = true;
      } else if (componentType.toLowerCase() === componentDTO["grid"]) {
        componentStatusCheck[componentDTO["grid"]].completed = true;
      } else if (componentType.toLowerCase() === componentDTO["controller"]) {
        componentStatusCheck[componentDTO["controller"]].completed = true;
      } else if (componentType.toLowerCase() === componentDTO["evcs"]) {
        componentStatusCheck[componentDTO["evcs"]].completed = true;
      }
    });
    setComponentStatus((prev) => ({
      ...prev,
      ...componentStatusCheck,
    }));
  };

  // On version change: reset atom. On unmount: block async writes + reset atom.
  useEffect(() => {
    isMountedRef.current = true;
    setProjectComponents(getDefaultComponentsState());
    return () => {
      isMountedRef.current = false;
      setProjectComponents(getDefaultComponentsState());
    };
  }, [designVersionId])

  useEffect(() => {
    const prev = prevSelectedDesignAndDesignVersion;
    const prevDesignVersionId = prevDesignVersionIdRef.current;

    // const prevSelectedVersion =
    const hasPrev =
      prev && prev.componentDTOList && prev.componentDTOList.length > 0;
    const hasCurrent =
      projectDetails &&
      selectedDesignAndDesignVersion.componentDTOList &&
      selectedDesignAndDesignVersion.componentDTOList.length > 0;
    // Always re-initialize when designVersionId changes
    if (!initializedRef.current && !hasPrev && hasCurrent) {
      if (filteredComponentDTOList.length) {
        initializeProgress();
      } else {
        createComponentStatus();
      }

      initializedRef.current = true;
    }
    if (prevDesignVersionId !== designVersionId) {
      // A genuine switch (not the very first mount) is only true once
      // prevDesignVersionId has actually been recorded before.
      const isVersionSwitch = prevDesignVersionId !== undefined;
      if (filteredComponentDTOList.length) {
        initializeProgress(isVersionSwitch);
      } else {
        // Reset to default state when filteredComponentDTOList is empty
        setProjectComponents(getDefaultComponentsState());
        createComponentStatus();
      }
    }
    prevProjectDetailsRef.current = projectDetails;
    prevDesignVersionIdRef.current = designVersionId;
  }, [projectDetails, designVersionId]);

  //  useEffect(() => {
  //     // const prev = prevProjectDetailsRef.current;
  //     // const hasPrev = prev && prev.componentDTOList && prev.componentDTOList.length > 0;
  //     // const hasCurrent = projectDetails && selectedDesignAndDesignVersion.componentDTOList && selectedDesignAndDesignVersion.componentDTOList.length > 0;
  //     // Always re-initialize when designVersionId changes
  //       if (filteredComponentDTOList.length) {
  //         initializeProgress();
  //       } else {
  //         createComponentStatus();
  //       }

  //     initializedRef.current = true;

  //   }, [ projectDetails, designVersionId]);

  const updateProgressComponent = () => {
    safeSetCurrentComponent(
      componentDTO[lastComponent?.componentType.toLowerCase()]
    );
    setActiveComponent(
      componentDTO[lastComponent?.componentType.toLowerCase()]
    );
  };

  const handleComponentClick = (component, newComponent = false) => {
    setAboutComponentSelected(component);
    if (
      initializedRef.current &&
      activeState === "DESIGN" &&
      componentDetails.currentComponent !== component &&
      componentStatus[componentDetails.currentComponent]?.isEditing &&
      !componentStatus[componentDetails.currentComponent]?.completed
    ) {
      setErrorModalMessage(getComponentDisplayName(componentDetails.currentComponent));
      setErrorModalOpen(true);
      return false;
    }
    if (
      !initializedRef.current ||
      activeState !== "DESIGN" ||
      !componentStatus[componentDetails.currentComponent]?.isEditing
    ) {
      selectComponentHandler(component, newComponent);
    }
  };

  const loadMethodRef = useRef(null);
  const loadGridRef = useRef(null);
  const pvRef = useRef(null);
  const batteryRef = useRef(null);
  const gensetRef = useRef(null);
  const controllerRef = useRef(null);
  const evcsRef = useRef(null);
  // Ref for sticky progress container (wraps ManageProgressBar + ComponentProgressBar)
  const progressStickyRef = useRef(null);
  const [tour] = useAtom(tourAtom);
  const stickyWrapperRef = useRef(null);
  // Dynamic sticky removal while highlighted tour step is inside
  useEffect(() => {
    const wrapper = stickyWrapperRef.current;
    if (!wrapper) return;

    if (!tour) {
      // restore default when tour inactive
      wrapper.style.position = "sticky";
      return;
    }

    const apply = () => {
      const active = wrapper.querySelector(".tour-highlight");
      wrapper.style.position = active ? "" : "sticky";
    };
    apply();
    const observer = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "attributes" && m.attributeName === "class") {
          apply();
          return;
        }
        if (m.type === "childList") {
          apply();
          return;
        }
      }
    });
    observer.observe(wrapper, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [tour]);
  const triggerLoadMethod = async () => {
    setErrorModalOpen(false);
    if (isVersionReadOnly) {
      displayError("error", t("progressBar.editingRestricted"));
      return;
    }

    if (loadMethodRef.current && componentDetails.currentComponent === "load") {
      await loadMethodRef.current.handleLoadMethod();
    } else if (
      loadGridRef.current &&
      componentDetails.currentComponent === "grid"
    ) {
      // Pre-validate outage selections before grid save; show snackbar and abort if invalid
      const outageValidation = loadGridRef.current.validateOutageBeforeSave?.();
      if (outageValidation?.error) {
        setSnackBar({ type: 'error', message: outageValidation.message });
        snackBarRef.current.open = true;
        return; // Block save until user fixes outage selections
      }
      await loadGridRef.current.handleLoadGridSubmit();
    } else if (pvRef.current && componentDetails.currentComponent === "pv") {
      await pvRef.current.handlePvSubmit();
    } else if (
      batteryRef.current &&
      componentDetails.currentComponent === "battery"
    ) {
      await batteryRef.current.handleBatterySubmit();
    } else if (
      gensetRef.current &&
      componentDetails.currentComponent === "genset"
    ) {
      await gensetRef.current.handleGensetSubmit();
    } else if (
      controllerRef.current &&
      componentDetails.currentComponent === "controller"
    ) {
      await controllerRef.current.handleControllerSubmit();
    } else if (
      evcsRef.current &&
      componentDetails.currentComponent === "evcs"
    ) {
      await evcsRef.current.handleEvcsMethod();
    } else {
      console.log("loadMethodRef is null");
    }
  };

  // Expose triggerLoadMethod to the rest of the app (e.g. SideMenu's
  // version-switch-blocked "Save" action) via a shared atom, since the
  // per-component form refs above only exist locally in this component.
  // Only re-registered when the active component changes (not on every
  // render) to avoid an infinite update loop from the new function identity.
  const [, setSaveTrigger] = useAtom(saveTriggerAtom);
  useEffect(() => {
    setSaveTrigger(() => triggerLoadMethod);
    return () => setSaveTrigger(null);
  }, [componentDetails.currentComponent, setSaveTrigger]);

  const onOk = (stepToRemove) => {
    return new Promise((resolve, reject) => {
      const removableComponent =
        selectedDesignAndDesignVersion.componentDTOList.filter(
          (component) =>
            componentDTO[component.componentType.toLowerCase()] ===
            stepToRemove["step"].toLowerCase()
        );
      setLoadingComp(true);
      if (removableComponent.length > 0) {
        MGA_API.deleteComponent(
          removableComponent[0].designId,
          removableComponent[0].designVersionId,
          removableComponent[0].componentId
        )
          .then((res) => {
            setProjectComponents((prev) => ({
              ...prev,
              [stepToRemove?.step]: cloneDeep(getDefaultComponentsState()[stepToRemove?.step] ?? {}),
            }));
            const updatedComponentSeries = addedSteps?.filter(
              (item) => item !== stepToRemove?.step
            );
            setAddedSteps(updatedComponentSeries);
            MGA_API.editProject(projectId)
              .then((res) => {
                setProjectDetails((prev) => ({
                  ...prev,
                  userId: res.userId || "",
                  currencyCode: res?.projectDTO?.currencyCode || "$",
                  projectDTO: res.projectDTO || {},
                  componentDTOList:
                    flattenCountComponentDTOList(res.countComponentDTOList) ||
                    [],
                  designWithVersionsDTOList:
                    res.designWithVersionsDTOList || [],
                  locationDTO: res.locationDTO || {},
                  countComponentDTOList: res.countComponentDTOList,
                  designComponentsList:
                    flattenDesignCountComponent(res.designComponentsList) || [],
                }));
                setLoadingComp(false);
                  const removedKey = String(stepToRemove?.step || "").toLowerCase();
                  setComponentStatus((prev) => ({
                    ...prev,
                    [removedKey]: {
                      completed: true,
                      isEditing: false,
                    },
                  }));                  
                  setProjectComponents((prev) => ({
                    ...prev,
                    currentComponent: componentDTO["load"],
                  }));
                setActiveComponent("load");
                try {
                  selectComponentHandler("load", false);
                } catch (e) {
                  handleComponentClick("load");
                }
                resolve(true);
              })
              .catch((error) => {
                setLoadingComp(false);
                displayError('error', getErrorMessage(error));
                console.log("Error editing project:", error);
                reject(false); // Reject with false on failure
              });
          })
          .catch((error) => {
            setLoadingComp(false);
            displayError('error', getErrorMessage(error));
            console.log("Error deleting component:", error);
            reject(false); // Reject with false on failure
          });
      } else {
        setLoadingComp(false);
        resolve(false);
      }
    });
  };

  const handleDeleteSimulation = async () => {
    setLoadingComp(true);
    try {
      await MGA_API.deleteSimulation(projectId, designId, designVersionId);
      const res = await MGA_API.editProject(projectId);
      setProjectDetails((prev) => ({
        ...prev,
        userId: res.userId || "",
        currencyCode: res?.projectDTO?.currencyCode || "$",
        projectDTO: res.projectDTO || {},
        componentDTOList: flattenCountComponentDTOList(res.countComponentDTOList) || [],
        designWithVersionsDTOList: res.designWithVersionsDTOList || [],
        locationDTO: res.locationDTO || {},
        countComponentDTOList: res.countComponentDTOList,
        designComponentsList: flattenDesignCountComponent(res.designComponentsList) || [],
      }));
      displayError("success", t("progressBar.simulationResultsRemoved"));
      return true;
    } catch (error) {
      displayError("error", getErrorMessage(error));
      return false;
    } finally {
      setLoadingComp(false);
    }
  };
  const { colorScheme } = useMantineColorScheme();

  return (
    <>
      <SEConfirmModal
        opened={errorModalOpen}
        onClose={() => {
          const currentComp = componentDetails?.currentComponent;
          const isComponentPresent = _.some(
            selectedDesignAndDesignVersion?.countComponentDTOList,
            (item) =>
              componentDTO?.[item.componentType]?.toLowerCase() ===
              currentComp?.toLowerCase()
          );
          if (!isComponentPresent) {
            const removeComp = addedSteps?.filter(
              (item) => item !== currentComp
            );
            setAddedSteps(removeComp);
          }
          // Revert any unsaved component entries (componentId: -1) that were added
          // locally (e.g. via PV addTab) without saving, so that on returning to
          // the component the tab list is back to its last-saved state.
          const compTypeUpper = currentComp?.toUpperCase();
          setProjectDetails((prev) => ({
            ...prev,
            designComponentsList: (prev.designComponentsList || []).map((design) => {
              if (design.designId?.toString() === designId?.toString()) {
                return {
                  ...design,
                  designVersionComponents: (design.designVersionComponents || []).map((version) => {
                    if (version.designVersionId?.toString() === designVersionId?.toString()) {
                      const updatedList = (version.countComponentDTOList || []).map((item) => {
                        if (item.componentType === compTypeUpper) {
                          const savedData = (item.data || []).filter((c) => c.componentId !== -1);
                          return { ...item, data: savedData, count: savedData.length };
                        }
                        return item;
                      });
                      return { ...version, countComponentDTOList: updatedList };
                    }
                    return version;
                  }),
                };
              }
              return design;
            }),
          }));
          if (!addedSteps.includes(aboutComponentSelected.toLowerCase())) {
            setActiveComponent(aboutComponentSelected.toLowerCase());
            setComponentStatus((prev) => ({
              ...prev,
              [aboutComponentSelected.toLowerCase().replace(/"/g, "")]: {
                completed: false,
                isEditing: true,
              },
            }));
          }
          selectComponentHandler(aboutComponentSelected);
          setErrorModalOpen(false);
        }}
        onConfirm={() => setErrorModalOpen(false)}
        header={"Warning"}
        confirmSeColor="standard"
        message={
          <Stack spacing={4}>
            <Text>
              <Trans
                i18nKey="manageProgress.unsavedEdit.message"
                values={{ component: errorModalMessage }}
                components={{ b: <strong /> }}
              />
            </Text>
            <Text>{t("manageProgress.unsavedEdit.okHint")}</Text>
            <Text>{t("manageProgress.unsavedEdit.cancelHint")}</Text>
            <Text>{t("manageProgress.unsavedEdit.saveHint")}</Text>
          </Stack>
        }
        confirmLabel={t('common.ok')}
        cancelLabel={t('common.cancel')}
        onExtra={
          !isVersionReadOnly
            ? triggerLoadMethod
            : undefined
        }
        extraLabel={t('common.save')}
      />
      <div className="col-sm-12 pl-0">
        <div
          ref={stickyWrapperRef}
          style={{
            position: "sticky",
            top: "0px",
            zIndex: "10",
            minHeight: "165px",
            backgroundColor:
              colorScheme === "dark"
                ? "var(--se-background-alternative)"
                : "#ffffff",
          }}
        >
          <ManageProgressBar />
          {activeState === "DESIGN" && (
            <ComponentProgressBar
              componentDTOList={componentDTOList}
              stepProgress={stepProgress}
              addedSteps={addedSteps}
              handleComponentClick={handleComponentClick}
              setShowNextSteps={setShowNextSteps}
              showNextSteps={showNextSteps}
              activeComponent={activeComponent}
              triggerLoadMethod={triggerLoadMethod}
              setAddedSteps={setAddedSteps}
              setActiveComponent={setActiveComponent}
              updateProgressComponent={updateProgressComponent}
              lastComponent={lastComponent}
              loading={loading}
              onOk={onOk}
              setSnackBar={setSnackBar}
              snackBarRef={snackBarRef}
              loadingComp={loadingComp}
              onDeleteSimulation={handleDeleteSimulation}
            />
          )}
        </div>
        <div className="col-sm-12 position-relative components-container">
          {loading || componentsLoader ? (
            <div className="col-sm-12 pb-2">
              <AccordianSkeleton />
            </div>
          ) : ( 
            <>
              {activeComponent === "load" && (
                <div
                  className="col-sm-12 pb-2"
                  style={{
                    pointerEvents:
                      isVersionReadOnly || !userScope?.access?.saveUpdateComponent
                        ? "none"
                        : "auto",
                    opacity: tour ? 1 : (isVersionReadOnly || !userScope?.access?.saveUpdateComponent ? 0.8 : 1),
                  }}
                >
                  <LoadMethod
                    projectDetails={projectDetails}
                    loadComponents={
                      selectedDesignAndDesignVersion?.componentDTOList
                    }
                    initialTab={"load"}
                    initialSelectedBuildingType={initialSelectedBuildingType}
                    ref={loadMethodRef}
                    setLoadingComp={setLoadingComp}
                    loadingComp={loadingComp}
                  />
                </div>
              )}
              {activeComponent === "grid" && (
                <div className=" col-sm-12"  style={{
                    pointerEvents:
                      isVersionReadOnly || !userScope?.access?.saveUpdateComponent
                        ? "none"
                        : "auto",
                      opacity: tour ? 1 : (isVersionReadOnly || !userScope?.access?.saveUpdateComponent ? 0.8 : 1),
                  }}>
                  <LoadGrid
                    component={gridComponent}
                    projectDetails={projectDetails}
                    tariffRef={tariffRef}
                    tariffTouRef={tariffTouRef}
                    tariffEnergyRef={tariffEnergyRef}
                    ref={loadGridRef}
                    dcTariffRef={dcTariffRef}
                    setLoadingComp={setLoadingComp}
                    loadingComp={loadingComp}
                    dcGenabilityTariffRef={dcGenabilityTariffRef}
                  />
                </div>
              )}
              {activeComponent === "pv" && (
                <div className=" col-sm-12"  style={{
                    pointerEvents:
                      isVersionReadOnly || !userScope?.access?.saveUpdateComponent
                        ? "none"
                        : "auto",
                      opacity: tour ? 1 : (isVersionReadOnly || !userScope?.access?.saveUpdateComponent ? 0.8 : 1),
                  }}>
                  <LoadPV
                    pvComponent={pvComponent}
                    projectDetails={projectDetails}
                    ref={pvRef}
                    setLoadingComp={setLoadingComp}
                    loadingComp={loadingComp}
                  />
                </div>
              )}
              {activeComponent === "battery" && (
                <div className=" col-sm-12"  style={{
                    pointerEvents:
                      isVersionReadOnly || !userScope?.access?.saveUpdateComponent
                        ? "none"
                        : "auto",
                      opacity: tour ? 1 : (isVersionReadOnly || !userScope?.access?.saveUpdateComponent ? 0.8 : 1),
                  }}>
                  <LoadBattery
                    ref={batteryRef}
                    setLoadingComp={setLoadingComp}
                    loadingComp={loadingComp}
                  />
                </div>
              )}
              {activeComponent === "genset" && (
                <div className=" col-sm-12"  style={{
                    pointerEvents:
                      isVersionReadOnly || !userScope?.access?.saveUpdateComponent
                        ? "none"
                        : "auto",
                      opacity: tour ? 1 : (isVersionReadOnly || !userScope?.access?.saveUpdateComponent ? 0.8 : 1),
                  }}>
                  <LoadGenset
                    ref={gensetRef}
                    setLoadingComp={setLoadingComp}
                    loadingComp={loadingComp}
                    onNavigateToComponent={selectComponentHandler}
                  />
                </div>
              )}
              {activeComponent === "controller" && (
                <div className=" col-sm-12"  style={{
                    pointerEvents:
                      isVersionReadOnly || !userScope?.access?.saveUpdateComponent
                        ? "none"
                        : "auto",
                      opacity: tour ? 1 : (isVersionReadOnly || !userScope?.access?.saveUpdateComponent ? 0.8 : 1),
                  }}>
                  <LoadController
                    ref={controllerRef}
                    setLoadingComp={setLoadingComp}
                  />
                </div>
              )}
              {activeComponent === "evcs" && (
                <div className=" col-sm-12" style={{
                    pointerEvents:
                      isVersionReadOnly || !userScope?.access?.saveUpdateComponent
                        ? "none"
                        : "auto",
                      opacity: tour ? 1 : (isVersionReadOnly || !userScope?.access?.saveUpdateComponent ? 0.8 : 1),
                  }}>
                  <EVCSMethod
                    ref={evcsRef}
                    projectDetails={projectDetails}
                    evcsComponents={selectedDesignAndDesignVersion?.componentDTOList}
                    initialTab={"load"}
                    setLoadingComp={setLoadingComp}
                    loadingComp={loadingComp}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <SeLoading loading={loadingComp} />
    </>
  );
});

export default LoadProject;
